import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import {
  Shield, CheckCircle, XCircle, Award, User, BookOpen,
  Calendar, Building2, Loader2, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CertificateVerifyPage = () => {
  const { certificateNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [verificationData, setVerificationData] = useState(null);

  useEffect(() => {
    verifyCertificate();
  }, [certificateNumber]);

  const verifyCertificate = async () => {
    try {
      const res = await axios.get(`${API}/verify/${encodeURIComponent(certificateNumber)}`);
      setVerificationData(res.data);
    } catch (error) {
      setVerificationData({
        valid: false,
        message: 'Verification failed. Please try again.',
        certificate_number: certificateNumber
      });
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-slate-600">Verifying Certificate...</p>
          <p className="text-sm text-slate-400 mt-2">Please wait while we authenticate this document</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50">
      <Header />
      
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Verification Status Banner */}
        <div className={`rounded-2xl shadow-xl overflow-hidden ${
          verificationData?.valid 
            ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
            : 'bg-gradient-to-r from-red-500 to-rose-600'
        }`}>
          <div className="p-8 text-center text-white">
            {verificationData?.valid ? (
              <>
                <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur">
                  <CheckCircle className="w-14 h-14 text-white" />
                </div>
                <h1 className="text-3xl font-bold mb-2">Certificate Verified</h1>
                <p className="text-white/80">This certificate is authentic and valid</p>
              </>
            ) : (
              <>
                <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur">
                  <XCircle className="w-14 h-14 text-white" />
                </div>
                <h1 className="text-3xl font-bold mb-2">Verification Failed</h1>
                <p className="text-white/80">{verificationData?.message || 'Certificate not found in our records'}</p>
              </>
            )}
          </div>
        </div>

        {/* Certificate Details */}
        {verificationData?.valid && (
          <div className="bg-white rounded-2xl shadow-lg mt-6 overflow-hidden">
            {/* Certificate Number Header */}
            <div className="bg-slate-50 border-b border-slate-200 p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Certificate Number</p>
              <p className="text-lg font-mono font-bold text-slate-800">{verificationData.certificate_number}</p>
            </div>

            {/* Details Grid */}
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Student Name */}
                <DetailRow 
                  icon={<User className="w-5 h-5 text-blue-500" />}
                  label="Student Name"
                  value={verificationData.student_name}
                />

                {/* Course */}
                <DetailRow 
                  icon={<BookOpen className="w-5 h-5 text-purple-500" />}
                  label="Course / Qualification"
                  value={verificationData.course_name}
                />

                {/* Certificate Type */}
                <DetailRow 
                  icon={<Award className="w-5 h-5 text-yellow-500" />}
                  label="Certificate Type"
                  value={verificationData.certificate_type === 'RPL' 
                    ? 'Recognition of Prior Learning (RPL)' 
                    : 'Short Term Training (STT)'}
                />

                {/* Grade */}
                <DetailRow 
                  icon={<Shield className="w-5 h-5 text-green-500" />}
                  label="Grade Achieved"
                  value={`${verificationData.grade} (${verificationData.percentage?.toFixed(1)}%)`}
                  valueClass={verificationData.grade === 'A+' ? 'text-green-600' : ''}
                />

                {/* Training Center */}
                {verificationData.center_name && (
                  <DetailRow 
                    icon={<Building2 className="w-5 h-5 text-orange-500" />}
                    label="Training Center"
                    value={verificationData.center_name}
                  />
                )}

                {/* Issue Date */}
                <DetailRow 
                  icon={<Calendar className="w-5 h-5 text-indigo-500" />}
                  label="Date of Issue"
                  value={formatDate(verificationData.date_of_issue)}
                />
              </div>

              {/* Status Badge */}
              <div className="mt-6 pt-6 border-t border-slate-200 flex items-center justify-center">
                <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full ${
                  verificationData.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {verificationData.status === 'active' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertTriangle className="w-5 h-5" />
                  )}
                  <span className="font-semibold">
                    Certificate Status: {verificationData.status?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Notice */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800 mb-1">Verification Notice</h3>
              <p className="text-sm text-blue-700">
                This verification page confirms the authenticity of certificates issued by IQRA Skills Pvt Ltd. 
                If you have any concerns about a certificate, please contact us at{' '}
                <a href="mailto:verify@iqraskills.org" className="underline">verify@iqraskills.org</a>
              </p>
            </div>
          </div>
        </div>

        {/* Searched Certificate Number (for invalid) */}
        {!verificationData?.valid && (
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Searched Certificate Number: <code className="bg-slate-100 px-2 py-1 rounded">{certificateNumber}</code>
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

// Detail Row Component
const DetailRow = ({ icon, label, value, valueClass = '' }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-slate-100 rounded-lg">
      {icon}
    </div>
    <div>
      <p className="text-xs text-slate-500 mb-0.5">{label}</p>
      <p className={`font-semibold text-slate-800 ${valueClass}`}>{value}</p>
    </div>
  </div>
);

export default CertificateVerifyPage;
